import { useCallback, useEffect, useRef, useState } from "react";
import {X, Download, Check, Loader2} from 'lucide-react';
import toast from "react-hot-toast";
import { CONTRACT_LABELS, ContractType } from "@/data/jobs";

/**
 * JobShareImageModal — Générateur de visuels de partage social (LinkedIn / Facebook).
 *
 * 100% navigateur via <canvas> : instantané, gratuit, fonctionne pour TOUTES les offres
 * (y compris futures). L'utilisateur choisit l'un des 3 modèles, prévisualise en direct,
 * puis télécharge un PNG 1200×630 prêt à uploader manuellement sur les réseaux.
 *
 * Aucune dépendance externe, aucune logique métier impactée : composant autonome.
 */

export interface JobShareData {
  title: string;
  employer: string;
  location: string;
  department?: string;
  category: "BNSSA" | "MNS" | string;
  contractType: string;
}

interface JobShareImageModalProps {
  job: JobShareData;
  onClose: () => void;
}

type TemplateId = "editorial" | "diagonal" | "minimal";

const TEMPLATES: { id: TemplateId; label: string; hint: string }[] = [
  { id: "editorial", label: "Éditorial sombre", hint: "Fond ardoise + accroche" },
  { id: "diagonal", label: "Bandeau couleur", hint: "Bloc univers en diagonale" },
  { id: "minimal", label: "Minimaliste clair", hint: "Fond clair, accent latéral" },
];

const W = 1200;
const H = 630;

// Couleurs de marque
const RED = "#e11d48";
const BLUE = "#2563eb";
const SLATE = "#1e293b";
const GREY = "#94a3b8";

// ─── Helpers de rendu canvas ────────────────────────────────────────────────

/** Dessine du texte multi-lignes en wrappant sur une largeur max ; renvoie la hauteur totale. */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines = 4
): number {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);

  const shown = lines.slice(0, maxLines);
  if (lines.length > maxLines && shown.length) {
    let last = shown[shown.length - 1];
    while (ctx.measureText(`${last}…`).width > maxWidth && last.length > 1) {
      last = last.slice(0, -1);
    }
    shown[shown.length - 1] = `${last}…`;
  }

  shown.forEach((line, i) => {
    ctx.fillText(line, x, y + i * lineHeight);
  });
  return shown.length * lineHeight;
}

/** Dessine le wordmark "bnssa-mns.fr" coloré. Renvoie la largeur dessinée. */
function drawWordmark(ctx: CanvasRenderingContext2D, x: number, y: number, fontPx: number, dotColor = GREY) {
  const font = `800 ${fontPx}px Archivo, Inter, system-ui, sans-serif`;
  ctx.font = font;
  ctx.textBaseline = "alphabetic";
  let cx = x;
  const parts: { t: string; c: string }[] = [
    { t: "bnssa", c: RED },
    { t: "-", c: dotColor },
    { t: "mns", c: BLUE },
    { t: ".fr", c: dotColor },
  ];
  for (const p of parts) {
    ctx.fillStyle = p.c;
    ctx.fillText(p.t, cx, y);
    cx += ctx.measureText(p.t).width;
  }
  return cx - x;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ─── Modèles ────────────────────────────────────────────────────────────────

function renderEditorial(ctx: CanvasRenderingContext2D, job: JobShareData) {
  const isBNSSA = job.category === "BNSSA";
  const accent = isBNSSA ? RED : BLUE;
  const role = isBNSSA ? "BNSSA" : "Maître-Nageur Sauveteur";

  // Fond
  ctx.fillStyle = SLATE;
  ctx.fillRect(0, 0, W, H);

  // Glow d'angle
  const glow = ctx.createRadialGradient(W - 120, 120, 30, W - 120, 120, 480);
  glow.addColorStop(0, isBNSSA ? "rgba(225,29,72,0.28)" : "rgba(37,99,235,0.30)");
  glow.addColorStop(1, "rgba(30,41,59,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Wordmark
  drawWordmark(ctx, 80, 110, 38);

  // Badge contrat
  const badge = `OFFRE • ${(CONTRACT_LABELS[job.contractType as ContractType] || job.contractType || "").toUpperCase()}`;
  ctx.font = "700 22px Archivo, Inter, system-ui, sans-serif";
  const bw = ctx.measureText(badge).width + 36;
  ctx.fillStyle = isBNSSA ? "rgba(225,29,72,0.18)" : "rgba(37,99,235,0.18)";
  roundRect(ctx, 80, 150, bw, 44, 22);
  ctx.fill();
  ctx.fillStyle = accent;
  ctx.textBaseline = "middle";
  ctx.fillText(badge, 98, 173);

  // Accroche
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 68px Archivo, Inter, system-ui, sans-serif";
  const city = job.location ? ` à ${job.location}` : "";
  const headline = `On recherche un ${role}${city}`;
  wrapText(ctx, headline, 80, 320, W - 160, 78, 4);

  // Sous-ligne employeur + dept
  ctx.fillStyle = GREY;
  ctx.font = "600 30px Inter, system-ui, sans-serif";
  const dep = job.department ? ` • ${job.department}` : "";
  ctx.fillText(`${job.employer}${dep}`, 80, H - 90);

  // Ligne signature dégradée
  const line = ctx.createLinearGradient(80, 0, W - 80, 0);
  line.addColorStop(0, RED);
  line.addColorStop(0.5, "#a855f7");
  line.addColorStop(1, BLUE);
  ctx.fillStyle = line;
  ctx.fillRect(0, H - 10, W, 10);
}

function renderDiagonal(ctx: CanvasRenderingContext2D, job: JobShareData) {
  const isBNSSA = job.category === "BNSSA";
  const accent = isBNSSA ? RED : BLUE;
  const accentDark = isBNSSA ? "#9f1239" : "#1e3a8a";

  // Fond clair
  ctx.fillStyle = "#f8fafc";
  ctx.fillRect(0, 0, W, H);

  // Bloc diagonal coloré (côté droit)
  const grad = ctx.createLinearGradient(W * 0.45, 0, W, H);
  grad.addColorStop(0, accent);
  grad.addColorStop(1, accentDark);
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.moveTo(W * 0.5, 0);
  ctx.lineTo(W, 0);
  ctx.lineTo(W, H);
  ctx.lineTo(W * 0.34, H);
  ctx.closePath();
  ctx.fill();

  // Wordmark (zone claire)
  drawWordmark(ctx, 70, 100, 34);

  // Catégorie
  ctx.fillStyle = accent;
  ctx.font = "800 26px Archivo, Inter, system-ui, sans-serif";
  ctx.fillText(job.category.toUpperCase(), 70, 200);

  // Titre du poste (zone claire, foncé)
  ctx.fillStyle = "#0f172a";
  ctx.font = "800 56px Archivo, Inter, system-ui, sans-serif";
  const usedH = wrapText(ctx, job.title, 70, 270, W * 0.55, 64, 3);

  // Employeur + lieu
  ctx.fillStyle = "#475569";
  ctx.font = "600 28px Inter, system-ui, sans-serif";
  ctx.fillText(job.employer, 70, 270 + usedH + 30);
  ctx.fillStyle = "#64748b";
  ctx.font = "500 26px Inter, system-ui, sans-serif";
  const dep = job.department ? ` (${job.department})` : "";
  ctx.fillText(`${job.location}${dep}`, 70, 270 + usedH + 72);

  // Texte dans le bloc coloré
  ctx.save();
  ctx.translate(W * 0.78, H * 0.5);
  ctx.rotate(-Math.PI / 2);
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.font = "800 38px Archivo, Inter, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("ON RECRUTE", 0, 0);
  ctx.font = "600 22px Inter, system-ui, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fillText(CONTRACT_LABELS[job.contractType as ContractType] || job.contractType || "", 0, 38);
  ctx.restore();
  ctx.textAlign = "left";
}

function renderMinimal(ctx: CanvasRenderingContext2D, job: JobShareData) {
  const isBNSSA = job.category === "BNSSA";
  const accent = isBNSSA ? RED : BLUE;

  // Fond blanc
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  // Accent latéral épais
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, 18, H);

  // Subtil bloc teinté en bas
  ctx.fillStyle = isBNSSA ? "rgba(225,29,72,0.05)" : "rgba(37,99,235,0.05)";
  ctx.fillRect(18, H - 140, W - 18, 140);

  // Wordmark
  drawWordmark(ctx, 90, 110, 34);

  // Badge contrat (pill outline)
  const badge = (CONTRACT_LABELS[job.contractType as ContractType] || job.contractType || "").toUpperCase();
  ctx.font = "700 22px Archivo, Inter, system-ui, sans-serif";
  const bw = ctx.measureText(badge).width + 36;
  ctx.lineWidth = 2;
  ctx.strokeStyle = accent;
  roundRect(ctx, 90, 150, bw, 42, 21);
  ctx.stroke();
  ctx.fillStyle = accent;
  ctx.textBaseline = "middle";
  ctx.fillText(badge, 108, 172);
  ctx.textBaseline = "alphabetic";

  // Titre du poste, grand et foncé
  ctx.fillStyle = "#0f172a";
  ctx.font = "800 58px Archivo, Inter, system-ui, sans-serif";
  const usedH = wrapText(ctx, job.title, 90, 300, W - 180, 66, 3);

  // Employeur
  ctx.fillStyle = "#334155";
  ctx.font = "600 30px Inter, system-ui, sans-serif";
  ctx.fillText(job.employer, 90, 300 + usedH + 26);

  // Lieu (dans le bloc teinté bas)
  ctx.fillStyle = accent;
  ctx.font = "700 28px Inter, system-ui, sans-serif";
  const dep = job.department ? ` • ${job.department}` : "";
  ctx.fillText(`📍 ${job.location}${dep}`, 90, H - 60);
}

const RENDERERS: Record<TemplateId, (ctx: CanvasRenderingContext2D, job: JobShareData) => void> = {
  editorial: renderEditorial,
  diagonal: renderDiagonal,
  minimal: renderMinimal,
};

// ─── Composant ──────────────────────────────────────────────────────────────

export default function JobShareImageModal({ job, onClose }: JobShareImageModalProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [template, setTemplate] = useState<TemplateId>("editorial");
  const [fontsReady, setFontsReady] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Attendre que les polices (Archivo/Inter) soient chargées pour un rendu net
  useEffect(() => {
    let active = true;
    const ready = () => active && setFontsReady(true);
    if (typeof document !== "undefined" && (document as Document & { fonts?: FontFaceSet }).fonts?.ready) {
      (document as Document & { fonts: FontFaceSet }).fonts.ready.then(ready).catch(ready);
      // Garde-fou si l'event ne se déclenche pas
      const t = window.setTimeout(ready, 800);
      return () => {
        active = false;
        window.clearTimeout(t);
      };
    }
    ready();
    return () => {
      active = false;
    };
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    RENDERERS[template](ctx, job);
  }, [template, job]);

  useEffect(() => {
    if (fontsReady) draw();
  }, [draw, fontsReady]);

  const handleDownload = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setDownloading(true);
    try {
      const url = canvas.toDataURL("image/png");
      const safe = (job.title || "offre")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 50);
      const a = document.createElement("a");
      a.href = url;
      a.download = `visuel-${safe || "offre"}-${template}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Visuel téléchargé ✓", { duration: 2500 });
    } catch (e) {
      console.error("[JobShareImageModal] download failed", e);
      toast.error("Téléchargement impossible. Réessayez.");
    } finally {
      setDownloading(false);
    }
  }, [job.title, template]);

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-5 flex items-start sm:items-center justify-between gap-3 z-10">
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-bold text-gray-900">Visuel de partage</h2>
            <p className="text-xs text-gray-500 truncate">
              {job.title} — {job.employer}
            </p>
          </div>
          <button onClick={onClose} aria-label="Fermer" className="p-1.5 hover:bg-gray-100 rounded-md flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          {/* Sélecteur de modèle */}
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">Choisissez un modèle</p>
            <div className="grid grid-cols-3 gap-2">
              {TEMPLATES.map((t) => {
                const active = t.id === template;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    className={`relative text-left rounded-xl border-2 p-3 transition-all ${
                      active
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    {active && (
                      <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center">
                        <Check className="w-3.5 h-3.5" />
                      </span>
                    )}
                    <div className="text-sm font-bold text-gray-900">{t.label}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">{t.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Aperçu */}
          <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 relative">
            {!fontsReady && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Génération…
              </div>
            )}
            <canvas
              ref={canvasRef}
              width={W}
              height={H}
              className="w-full h-auto block"
              style={{ aspectRatio: `${W} / ${H}` }}
            />
          </div>

          {/* Action */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!fontsReady || downloading}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-bold px-5 py-3 rounded-lg transition-colors"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Télécharger le visuel (PNG 1200×630)
            </button>
          </div>
          <p className="text-[11px] text-gray-500 leading-relaxed">
            Téléchargez l'image sur votre ordinateur, puis ajoutez-la manuellement à votre publication
            LinkedIn ou Facebook. Le format 1200×630 est optimal pour les réseaux sociaux.
          </p>
        </div>
      </div>
    </div>
  );
}
